import { CheckIcon, CopyIcon } from "lucide-react";
import { useState } from "react";
import type { SubscriptionStatus } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { DotLoader } from "@/components/ui/spinner";
import { STATUS_LABELS } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";

type Tone = "neutral" | "good" | "warn" | "bad" | "info";

const TONES: Record<Tone, string> = {
	neutral: "text-muted-foreground",
	good: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
	warn: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400",
	bad: "border-destructive/30 bg-destructive/10 text-destructive",
	info: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400",
};

export function Pill({
	tone = "neutral",
	className,
	children,
}: {
	tone?: Tone;
	className?: string;
	children: React.ReactNode;
}) {
	return <Badge className={cn(TONES[tone], className)}>{children}</Badge>;
}

/** How a workspace pays: the one-word answer for lists and headers. */
export function TenantStatus({
	planId,
	suspendedAt,
	billedByStripe,
	status,
}: {
	planId: string;
	suspendedAt: string | null;
	billedByStripe: boolean | null;
	status: SubscriptionStatus | null;
}) {
	if (suspendedAt) return <Pill tone="bad">Suspended</Pill>;
	if (billedByStripe && status) {
		if (status === "active") return <Pill tone="good">Paying</Pill>;
		if (status === "trialing") return <Pill tone="info">Trialing</Pill>;
		if (status === "past_due" || status === "unpaid")
			return <Pill tone="warn">{STATUS_LABELS[status]}</Pill>;
		return <Pill>{STATUS_LABELS[status]}</Pill>;
	}
	if (planId !== "free") return <Pill tone="info">Comped</Pill>;
	return <Pill>Free</Pill>;
}

export function CopyId({ value, className }: { value: string; className?: string }) {
	const [copied, setCopied] = useState(false);
	return (
		<button
			type="button"
			title="Copy"
			onClick={() => {
				void navigator.clipboard.writeText(value).then(() => {
					setCopied(true);
					setTimeout(() => setCopied(false), 1200);
				});
			}}
			className={cn(
				"group inline-flex max-w-full items-center gap-1 font-mono text-[11px] text-muted-foreground hover:text-foreground",
				className,
			)}
		>
			<span className="truncate">{value}</span>
			{copied ? (
				<CheckIcon className="size-3 shrink-0" />
			) : (
				<CopyIcon className="size-3 shrink-0 opacity-0 group-hover:opacity-100" />
			)}
		</button>
	);
}

export function PageLoader() {
	return (
		<div className="flex flex-1 items-center justify-center py-24 text-muted-foreground">
			<DotLoader />
		</div>
	);
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
	return (
		<div className="flex flex-col items-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center">
			<span className="mono-label">Couldn't load</span>
			<p className="max-w-md text-[13px] text-muted-foreground">
				{error instanceof Error ? error.message : "Something went wrong"}
			</p>
			{onRetry && (
				<Button variant="outline" size="sm" onClick={onRetry}>
					Try again
				</Button>
			)}
		</div>
	);
}

/** Label / value pair for detail panels. */
export function Field({ label, children }: { label: string; children: React.ReactNode }) {
	return (
		<div className="flex min-w-0 flex-col gap-1">
			<dt className="mono-label">{label}</dt>
			<dd className="min-w-0 truncate text-[13px]">{children}</dd>
		</div>
	);
}
